import type { CityConfig } from "@/pages/city-template/types";

export const springfieldVtConfig: CityConfig = {
  city: "Springfield",
  state: "Vermont",
  stateAbbr: "VT",
  slug: "springfield-vt",
  route: "/springfield-vt",
  title: "Expedited Passport Services in Springfield, VT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Springfield, VT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Springfield, Chester, Ludlow, Bellows Falls, and all of Southern Windsor County.",
  h1: "Expedited Passport Services in Springfield, VT",
  heroSubtitle:
    "Springfield travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No trip to the Vermont Passport Agency required.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Historic%20Springfield%20Vermont%20downtown%20featuring%20the%20iconic%20Springfield%20Meetinghouse%20and%20historic%20buildings%20along%20Main%20Street%2C%20beautiful%20scenic%20view%20of%20the%20Connecticut%20River%20Valley%20with%20rolling%20green%20hills%20and%20lush%20forests%2C%20charming%20New%20England%20small%20town%20atmosphere%2C%20warm%20golden%20hour%20light%2C%20picturesque%20Southern%20Windsor%20County%20countryside%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=vt-spr-hero-001&orientation=landscape",
  heroImageAlt: "Springfield Vermont downtown with historic meetinghouse and Connecticut River Valley",
  geoPosition: "43.2994; -72.4828",
  geoPlacename: "Springfield, Vermont, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Vermont Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~200 miles from Springfield VT via I-91",
  },
  nearbyCitiesZips: [
    { name: "Springfield", zips: "05156" },
    { name: "Chester", zips: "05143" },
    { name: "Ludlow", zips: "05149" },
    { name: "Bellows Falls", zips: "05101" },
    { name: "Weathersfield", zips: "05151" },
    { name: "Ascutney", zips: "05030" },
    { name: "Windsor", zips: "05089" },
    { name: "Claremont", zips: "03743" },
    { name: "Charlestown", zips: "03603" },
    { name: "Newport", zips: "05050" },
    { name: "Croydon", zips: "05156" },
    { name: "Hartland", zips: "05048" },
    { name: "Woodstock", zips: "05091" },
    { name: "Rutland", zips: "05701, 05702" },
    { name: "Brattleboro", zips: "05301, 05302, 05303" },
    { name: "Bennington", zips: "05201, 05257" },
    { name: "Montpelier", zips: "05601, 05602, 05603, 05604, 05609, 05620, 05633" },
    { name: "Burlington", zips: "05401, 05402, 05405, 05406" },
    { name: "White River Junction", zips: "05001" },
    { name: "Hartford", zips: "05047, 05001" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Springfield travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Southern Windsor County",
      desc: "Springfield, Chester, Ludlow, Bellows Falls, and all surrounding Southern Windsor County communities.",
    },
  ],
  testimonials: [
    {
      name: "Linda W.",
      location: "Springfield, VT",
      rating: 5,
      text: "I needed my passport renewed for a trip to Ireland in 5 days. Fast Passport Center had it back to me in Springfield in 3 days. Their Southern Windsor County service is outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "Michael P.",
      location: "Chester, VT",
      rating: 5,
      text: "Applied for my first passport through Fast Passport Center. They helped me navigate the process from Chester and I had my passport in 4 days. Great experience.",
      service: "New Passport",
    },
    {
      name: "Jennifer T.",
      location: "Ludlow, VT",
      rating: 5,
      text: "My daughter needed a passport for a school trip to Quebec. Fast Passport Center handled the child passport process and had it ready in 4 days. Ludlow families can trust them.",
      service: "Child Passport",
    },
    {
      name: "William H.",
      location: "Bellows Falls, VT",
      rating: 5,
      text: "Lost my passport before a business trip to Europe. Fast Passport Center replaced it in 3 days and saved my trip. Bellows Falls residents should use them for emergencies.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "spr-1",
      name: "Springfield Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "12 Main Street",
      city: "Springfield",
      state: "VT",
      zip: "05156",
      phone: "802-885-2100",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Springfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "spr-2",
      name: "Bellows Falls Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "47 Church Street",
      city: "Bellows Falls",
      state: "VT",
      zip: "05101",
      phone: "802-463-9906",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Bellows Falls",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "spr-3",
      name: "Chester Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "13 Main Street",
      city: "Chester",
      state: "VT",
      zip: "05143",
      phone: "802-875-5114",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Chester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "spr-4",
      name: "Windsor Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "4 Main Street",
      city: "Windsor",
      state: "VT",
      zip: "05089",
      phone: "802-674-5383",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Windsor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "spr-5",
      name: "Ludlow Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "164 Main Street",
      city: "Ludlow",
      state: "VT",
      zip: "05149",
      phone: "802-228-8826",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Ludlow",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "spr-6",
      name: "Springfield Town Clerk",
      type: "Town Clerk / Passport Services",
      address: "96 Main Street",
      city: "Springfield",
      state: "VT",
      zip: "05156",
      phone: "802-885-2104",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "Springfield Municipal Office",
      website: "https://www.springfieldvt.gov/",
      websiteLabel: "Town of Springfield",
      notes: "Provides passport application acceptance services. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Springfield, VT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the Vermont Passport Agency if I live in Springfield VT?",
      a: "No — if you use Fast Passport Center, you don't need to visit the Vermont Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Springfield VT?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Springfield, Bellows Falls, and Chester. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Springfield VT?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Springfield VT?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Springfield VT?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Springfield. We serve Springfield and all of Southern Windsor County through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Springfield VT for international travel?",
      a: "Lebanon Municipal Airport (LEB) in New Hampshire is the closest at ~20 miles. Burlington International (BTV) is ~70 miles, Albany International (ALB) is ~80 miles, and Boston Logan (BOS) is ~130 miles. Most Springfield VT residents use Lebanon or Albany for international travel.",
    },
    {
      q: "How much does expedited passport service cost in Springfield VT?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Springfield VT?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Springfield VT?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Springfield VT?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Springfield VT have a local passport agency?",
      a: "No, Springfield does not have a regional passport agency. The nearest regional agency is the Vermont Passport Agency in Manhattan, approximately 200 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Springfield VT", address: "various CVS locations in Springfield VT", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Springfield VT", address: "various Walgreens locations in Springfield VT", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Springfield VT", address: "various UPS Store locations in Springfield VT", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Claremont NH", address: "various FedEx Office locations in Claremont NH", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Claremont NH", address: "various Walmart locations in Claremont NH", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "VT DMV — Springfield VT",
      address: "100 Mineral Street, Springfield, VT 05156",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications. Closest DMV to Springfield VT.",
      featured: true,
    },
    {
      name: "VT DMV — White River Junction",
      address: "100 River Road, White River Junction, VT 05001",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Alternative DMV location for the Upper Valley.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "LEB",
      name: "Lebanon Municipal Airport",
      distance: "~20 miles from Springfield VT",
      address: "5 Airpark Rd, West Lebanon, NH 03784",
      note: "Closest airport to Springfield VT with Cape Air service to Boston. Limited commercial flights.",
      flightUrl: "https://www.lebanonairport.com/",
      flightLabel: "Lebanon Municipal",
      featured: true,
    },
    {
      code: "ALB",
      name: "Albany International Airport",
      distance: "~80 miles from Springfield VT",
      address: "737 Albany Shaker Rd, Albany, NY 12211",
      note: "Alternative for Springfield VT travelers with Southwest and other domestic carriers.",
      flightUrl: "https://www.albanyairport.com/",
      flightLabel: "Albany International",
      featured: false,
    },
    {
      code: "BTV",
      name: "Burlington International Airport",
      distance: "~70 miles from Springfield VT",
      address: "1200 Airport Dr, South Burlington, VT 05403",
      note: "Vermont's primary airport with more flight options. Common for Springfield VT residents.",
      flightUrl: "https://www.btv.aero/",
      flightLabel: "Burlington International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Springfield VT travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Springfield VT residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Springfield VT residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Springfield VT residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Springfield VT, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Springfield VT residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Springfield VT travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Springfield VT residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Springfield VT residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Springfield VT residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Springfield VT." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Springfield VT." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Springfield VT travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Springfield VT travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Springfield VT team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Springfield VT address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Springfield VT-Specific Guidance", agency: "Generic — no Southern Windsor County or Vermont expertise", fpc: "Full Springfield/Southern Windsor County knowledge built in" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Burlington", route: "/burlington-vt" },
    { name: "Montpelier", route: "/montpelier" },
    { name: "Rutland", route: "/rutland" },
    { name: "Stowe", route: "/stowe" },
    { name: "Brattleboro", route: "/brattleboro" },
    { name: "Bennington", route: "/bennington" },
    { name: "Middlebury", route: "/middlebury" },
    { name: "Woodstock", route: "/woodstock-vt" },
    { name: "Springfield", route: "/springfield-vt" },
    { name: "White River Junction", route: "/white-river-junction" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Springfield and all of Southern Windsor County as a remote courier service. We do not have a physical office in Springfield, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "50 South Main Street, St. Albans, VT 05478",
  fpcOfficeDistance: "Remote courier service — Springfield, VT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Springfield neighborhoods and surrounding Southern Windsor County communities. From downtown to the North End, from the Connecticut River to the Ascutney foothills — we serve every Springfield VT community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Springfield and all surrounding Southern Windsor County communities. Whether you're in downtown Springfield or a nearby town, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Springfield VT customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Springfield VT travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Springfield and Southern Windsor County travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Springfield, VT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/vermont",
  statePageName: "Vermont Passport Services",
  dmvWebsite: "https://dmv.vermont.gov",
  dmvLabel: "Vermont DMV — Official Website",
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Vermont Department of Health Vital Records in Burlington. Required for first-time passport applications.", url: "https://www.healthvermont.gov/vital-records" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Vermont DPH Vital Records office.", url: "https://www.healthvermont.gov/vital-records" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Springfield Town Clerk at 96 Main Street. Required for name change passport applications.", url: "https://www.springfieldvt.gov/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Windsor County Family Court. Required for name change passport corrections.", url: "https://www.vermontjudiciary.org/courts/family-division" },
  ],
};

export default springfieldVtConfig;